<script lang="ts">
  /**
   * Global Bot Tour Controller — UX v2
   *
   * Montado permanentemente em App.svelte.
   * Fases: idle → running (banner + overlays animados) → finished (tela de conversão)
   *
   * Segurança: tour nunca dura mais de 90s. "Continuar como visitante" só fica
   * disponível na tela final e abre uma sessão demo regular (sem escopos bot_tour).
   */
  import { onMount, onDestroy } from "svelte";
  import { fly, fade } from "svelte/transition";
  import { navigate, router } from "$lib/router.svelte";
  import { setAccessToken, api } from "$lib/api/client";
  import { auth } from "$lib/stores/auth.svelte";

  // ── Metadados por rota ─────────────────────────────────────────
  // Ordem é a do tour plan (dashboard → insights → transactions → …)
  const TOUR_STEPS: Record<string, { icon: string; section: string; title: string; text: string }> = {
    "/dashboard": {
      icon: "📊",
      section: "Visão geral",
      title: "Tudo numa tela",
      text: "Saldo atual, entradas, saídas e pendências do mês — em tempo real, sem precisar calcular nada.",
    },
    "/insights": {
      icon: "🧠",
      section: "Inteligência",
      title: "Entenda para onde seu dinheiro vai",
      text: "A IA analisa seus padrões, aponta onde você gasta mais do que deveria e sugere ajustes.",
    },
    "/transactions": {
      icon: "⚡",
      section: "Lançamentos",
      title: "Registre em segundos",
      text: "Texto, voz, foto de nota fiscal ou mensagem no Telegram — a IA classifica e salva.",
    },
    "/recorrencias": {
      icon: "🔁",
      section: "Recorrências",
      title: "Contas fixas no piloto",
      text: "Aluguel, assinaturas, planos — alertas automáticos antes do vencimento.",
    },
    "/fidelidade": {
      icon: "⭐",
      section: "Fidelidade",
      title: "Saiba quando pode sair sem multa",
      text: "Controle de vigência de contratos. Economia real na hora de negociar.",
    },
    "/categories": {
      icon: "🏷️",
      section: "Categorias",
      title: "Organize do seu jeito",
      text: "Ícones, cores e subcategorias. Controle total sem impor estrutura rígida.",
    },
    "/workspaces": {
      icon: "🗂️",
      section: "Workspaces",
      title: "Pessoal, família, empresa",
      text: "Contextos separados com permissões diferentes. Sem misturar o que não deve.",
    },
    "/pricing": {
      icon: "💎",
      section: "Planos",
      title: "Comece grátis. Cresce com você.",
      text: "Free completo para uso pessoal. Premium libera IA avançada, exportações e backup.",
    },
    "/settings": {
      icon: "⚙️",
      section: "Configurações",
      title: "Você no controle total",
      text: "Tema escuro, notificações push, segurança por biometria e exportação dos seus dados.",
    },
  };

  type Phase = "idle" | "running" | "finished";

  let phase = $state<Phase>("idle");
  let remaining = $state(0);
  let progress = $state(0); // % de etapas concluídas (0-100)
  let overlayKey = $state(0); // incrementado a cada nova etapa → força re-animação

  type OverlayData = {
    icon: string;
    section: string;
    title: string;
    text: string;
    stepNum: number;
    totalSteps: number;
  };
  let currentOverlay = $state<OverlayData | null>(null);

  let plan: string[] = [];
  let step = 0;

  let stepTimer: ReturnType<typeof setTimeout> | undefined;
  let countdownTimer: ReturnType<typeof setInterval> | undefined;
  let overlayTimer: ReturnType<typeof setTimeout> | undefined;

  // ── Internos ───────────────────────────────────────────────────
  function clearTimers() {
    if (stepTimer !== undefined) clearTimeout(stepTimer);
    if (countdownTimer !== undefined) clearInterval(countdownTimer);
    if (overlayTimer !== undefined) clearTimeout(overlayTimer);
    stepTimer = countdownTimer = overlayTimer = undefined;
  }

  function clearSession() {
    sessionStorage.removeItem("takite.bot_tour");
    sessionStorage.removeItem("takite.bot_tour.plan");
    sessionStorage.removeItem("takite.bot_tour.deadline");
    setAccessToken(null);
  }

  function showOverlay(path: string) {
    const meta = TOUR_STEPS[path];
    if (!meta) { currentOverlay = null; return; }

    const stepNum = plan.indexOf(path) + 1;
    currentOverlay = { ...meta, stepNum, totalSteps: plan.length };
    progress = Math.round((stepNum / plan.length) * 100);
    overlayKey += 1;

    if (overlayTimer !== undefined) clearTimeout(overlayTimer);
    // Overlay visível por 8s — tempo de leitura + observação da tela
    overlayTimer = setTimeout(() => { currentOverlay = null; }, 8000);
  }

  function loop() {
    if (step >= plan.length) return;
    const next = plan[step];
    if (next) {
      navigate(next);
      showOverlay(next);
    }
    step += 1;
    const remainingSteps = Math.max(plan.length - step + 1, 1);
    const perStep = Math.max(4, Math.floor(Math.max(remaining - 4, 8) / remainingSteps));
    stepTimer = setTimeout(loop, perStep * 1000);
  }

  function startCountdown(deadline: number) {
    remaining = Math.max(0, Math.round((deadline - Date.now()) / 1000));
    countdownTimer = setInterval(() => {
      remaining = Math.max(0, Math.round((deadline - Date.now()) / 1000));
      if (remaining <= 0) finish();
    }, 1000);
  }

  function finish() {
    clearTimers();
    clearSession();
    currentOverlay = null;
    phase = "finished";
    // Não navega — a tela de conversão sobrepõe a interface.
  }

  function maybeActivate() {
    if (sessionStorage.getItem("takite.bot_tour") !== "1") return;
    const deadlineRaw = sessionStorage.getItem("takite.bot_tour.deadline");
    if (!deadlineRaw) return;
    const deadline = parseInt(deadlineRaw, 10);
    if (!Number.isFinite(deadline) || deadline <= Date.now()) { clearSession(); return; }

    try { plan = JSON.parse(sessionStorage.getItem("takite.bot_tour.plan") || "[]"); }
    catch { plan = []; }
    if (!plan.length) plan = ["/dashboard"];

    phase = "running";
    startCountdown(deadline);
    showOverlay(router.path);
    loop();
  }

  // ── CTAs da tela final ─────────────────────────────────────────
  async function continueAsVisitor() {
    try {
      const r = await api<{ access_token: string }>("/demo/login", { method: "POST" });
      setAccessToken(r.access_token);
      sessionStorage.setItem("takite.demo", "1");
      localStorage.setItem("takite.onboarded", "1");
      await auth.bootstrap();
      phase = "idle";
      navigate("/dashboard");
    } catch {
      phase = "idle";
      navigate("/landing");
    }
  }

  function goRegister() { phase = "idle"; navigate("/register"); }
  function goLanding() { phase = "idle"; navigate("/landing"); }
  async function goPricing() { await continueAsVisitor(); navigate("/pricing"); }

  onMount(() => {
    window.addEventListener("takite:bot-tour-start", maybeActivate as EventListener);
    maybeActivate();
  });

  onDestroy(() => {
    clearTimers();
    window.removeEventListener("takite:bot-tour-start", maybeActivate as EventListener);
  });
</script>

<!-- ── FASE: RUNNING ──────────────────────────────────────────── -->
{#if phase === "running"}

  <!-- Banner superior com barra de progresso de etapas -->
  <div
    class="fixed top-0 inset-x-0 z-[9999] shadow-lg select-none"
    role="status"
    aria-live="polite"
  >
    <div class="bg-amber-500 text-black text-xs sm:text-sm font-medium px-3 py-2 flex items-center justify-between gap-2">
      <div class="flex items-center gap-2 min-w-0">
        <span class="shrink-0">🤖</span>
        <span class="hidden sm:block truncate">
          {#if currentOverlay}
            {currentOverlay.icon}&nbsp;{currentOverlay.section}
          {:else}
            MODO ROBÔ — dados demonstrativos
          {/if}
        </span>
        <span class="sm:hidden truncate">MODO ROBÔ ATIVO</span>
      </div>
      <div class="flex items-center gap-2 shrink-0">
        {#if currentOverlay}
          <span class="text-[10px] text-black/50 tabular-nums">
            {currentOverlay.stepNum}/{currentOverlay.totalSteps}
          </span>
        {/if}
        <span class="tabular-nums font-bold">{remaining}s</span>
      </div>
    </div>
    <!-- Barra de progresso de etapas (não de tempo) -->
    <div class="h-[3px] bg-amber-300/40">
      <div
        class="h-full bg-amber-800/70 transition-all duration-700 ease-out"
        style="width: {progress}%"
      ></div>
    </div>
  </div>

  <!-- Overlay contextual animado (slide-up) -->
  {#key overlayKey}
    {#if currentOverlay}
      <div
        class="fixed bottom-6 inset-x-0 flex justify-center px-3 z-[9998] pointer-events-none"
        role="note"
        aria-live="polite"
        in:fly={{ y: 28, duration: 380, opacity: 0 }}
        out:fade={{ duration: 220 }}
      >
        <div class="max-w-sm w-full bg-slate-900/97 text-white rounded-2xl shadow-2xl border border-amber-400/30 backdrop-blur overflow-hidden">

          <!-- Cabeçalho: etapa + ícone -->
          <div class="flex items-center justify-between gap-2 px-4 pt-3 pb-0">
            <span class="text-[10px] font-bold tracking-widest text-amber-400 uppercase">
              Etapa {currentOverlay.stepNum} de {currentOverlay.totalSteps}
            </span>
            <span class="text-base">{currentOverlay.icon}</span>
          </div>

          <!-- Título + texto -->
          <div class="px-4 pt-1 pb-3">
            <div class="text-sm font-bold text-white leading-tight">{currentOverlay.title}</div>
            <div class="text-xs text-slate-300 mt-1 leading-relaxed">{currentOverlay.text}</div>
          </div>

          <!-- Dots de progresso -->
          <div class="flex items-center gap-[5px] px-4 pb-3">
            {#each { length: currentOverlay.totalSteps } as _, i}
              <div
                class="h-[3px] rounded-full transition-all duration-500 {
                  i + 1 === currentOverlay.stepNum
                    ? 'bg-amber-400 flex-[2]'
                    : i + 1 < currentOverlay.stepNum
                      ? 'bg-amber-600/50 flex-1'
                      : 'bg-slate-600 flex-1'
                }"
              ></div>
            {/each}
          </div>

        </div>
      </div>
    {/if}
  {/key}

{/if}

<!-- ── FASE: FINISHED ─────────────────────────────────────────── -->
{#if phase === "finished"}
  <div
    class="fixed inset-0 z-[9999] bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-950 flex items-center justify-center p-4 overflow-y-auto"
    role="dialog"
    aria-modal="true"
    aria-labelledby="bt-finish-title"
    in:fade={{ duration: 350 }}
  >
    <div
      class="max-w-md w-full space-y-5"
      in:fly={{ y: 32, duration: 480, delay: 200 }}
    >
      <!-- Card principal -->
      <div class="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-6 sm:p-8 space-y-5">

        <div class="text-center space-y-2">
          <div class="text-5xl">🎉</div>
          <h2 id="bt-finish-title" class="text-2xl font-bold text-slate-900 dark:text-white">
            Você acabou de ver o TáKite funcionando.
          </h2>
          <p class="text-sm text-slate-500 dark:text-slate-400">
            9 áreas em 90 segundos. Isso é só a superfície —
            <strong class="text-slate-700 dark:text-slate-200">a profundidade está esperando por você.</strong>
          </p>
        </div>

        <ul class="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-700 dark:text-slate-200">
          <li class="flex gap-2 items-start"><span class="shrink-0">✅</span><span>Lançamento por texto, voz e Telegram</span></li>
          <li class="flex gap-2 items-start"><span class="shrink-0">✅</span><span>IA que aponta onde o dinheiro vaza</span></li>
          <li class="flex gap-2 items-start"><span class="shrink-0">✅</span><span>Contas fixas no piloto automático</span></li>
          <li class="flex gap-2 items-start"><span class="shrink-0">✅</span><span>Pessoal, família e empresa separados</span></li>
        </ul>

        <div class="flex flex-col gap-2">
          <button
            class="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-semibold py-3 px-4 transition-colors shadow-lg shadow-emerald-900/20"
            onclick={goRegister}
          >
            🚀 Criar conta grátis
          </button>
          <div class="grid grid-cols-2 gap-2">
            <button
              class="rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 font-medium py-2.5 px-3 text-sm transition-colors"
              onclick={continueAsVisitor}
            >
              Explorar como visitante
            </button>
            <button
              class="rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 font-medium py-2.5 px-3 text-sm transition-colors"
              onclick={goPricing}
            >
              Ver planos
            </button>
          </div>
        </div>

      </div>

      <button
        class="block w-full text-center text-xs text-slate-400 hover:text-slate-200 underline-offset-2 hover:underline transition-colors"
        onclick={goLanding}
      >
        Voltar ao site
      </button>

    </div>
  </div>
{/if}
